import { relations, sql } from 'drizzle-orm'
import {
  bigint,
  boolean,
  check,
  index,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
} from 'drizzle-orm/pg-core'

import { modelStack } from './models'
export type { ProviderTier, ModelConfig } from './constants'
export { DEFAULT_MODEL_CONFIG } from './constants'

// ========================================
// USER TABLE (BetterAuth generated + minimal extended fields)
// ========================================

export const user = pgTable('user', {
  // BetterAuth core fields
  id: text('id').primaryKey(),
  name: text('name').notNull(),
  email: text('email').notNull().unique(),
  emailVerified: boolean('email_verified').notNull(),
  image: text('image'),
  createdAt: timestamp('created_at').notNull(),
  updatedAt: timestamp('updated_at').notNull(),

  // Optional: Add your custom user fields here
  username: text('username').unique(),

  // Admin plugin fields
  role: text('role'),
  banned: boolean('banned'),
  banReason: text('ban_reason'),
  banExpires: timestamp('ban_expires'),

  // Billing fields
  creditBalance: bigint('credit_balance', { mode: 'number' })
    .default(0)
    .notNull(), // In milli-dollars (total = prepaid + bonus)
  prepaidCreditBalance: bigint('prepaid_credit_balance', { mode: 'number' })
    .default(0)
    .notNull(), // Top-up balance with expiration (milli-dollars)
  bonusCreditBalance: bigint('bonus_credit_balance', { mode: 'number' })
    .default(0)
    .notNull(), // Referral/gift-code balance with 30-day expiration (milli-dollars)
  polarCustomerId: text('polar_customer_id'),
  lowBalanceNotifiedAt: timestamp('low_balance_notified_at'), // Set when the low-prepaid-balance email is sent; cleared once balance recovers above the threshold
  marketingOptOut: boolean('marketing_opt_out').notNull().default(false), // Set via the unsubscribe link in marketing emails; transactional email is unaffected

  // Referral fields
  referralCode: text('referral_code'), // Code this user signed up with or entered manually

  // Playground internal API key (encrypted)
  internalPlaygroundApiKeyEncrypted: text(
    'internal_playground_api_key_encrypted',
  ),
}, (table) => [
  check(
    'balance_invariant_check',
    sql`${table.creditBalance} = ${table.prepaidCreditBalance} + ${table.bonusCreditBalance}`,
  ),
  // Wallets may go slightly negative: settlement runs asynchronously after the
  // response was already served, and the pre-flight risk buffer deliberately
  // admits requests short by up to 400 milli-dollars. Forbidding negative here
  // would make settlement fail and dead-letter usage we never billed.
  //
  // The floor still bounds the damage, so an accounting bug (e.g. an expiry job
  // double-deducting) trips loudly rather than drifting. -500 milli-dollars =
  // MAX_OVERDRAFT_MILLIDOLLARS in shared-pricing-config.ts — keep in sync.
  check(
    'prepaid_balance_floor',
    sql`${table.prepaidCreditBalance} >= -500`,
  ),
  check(
    'bonus_balance_floor',
    sql`${table.bonusCreditBalance} >= -500`,
  ),
])

// ========================================
// SESSION TABLE (BetterAuth generated)
// ========================================

export const session = pgTable(
  'session',
  {
    id: text('id').primaryKey(),
    expiresAt: timestamp('expires_at').notNull(),
    token: text('token').notNull().unique(),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at')
      .defaultNow()
      .$onUpdate(() => new Date())
      .notNull(),
    ipAddress: text('ip_address'),
    userAgent: text('user_agent'),
    userId: text('user_id')
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),
    impersonatedBy: text('impersonated_by'),
  },
  (table) => [index('session_userId_idx').on(table.userId)],
)

// ========================================
// ACCOUNT TABLE (BetterAuth generated - OAuth providers)
// ========================================

export const account = pgTable(
  'account',
  {
    id: text('id').primaryKey(),
    accountId: text('account_id').notNull(),
    providerId: text('provider_id').notNull(),
    userId: text('user_id')
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),
    accessToken: text('access_token'),
    refreshToken: text('refresh_token'),
    idToken: text('id_token'),
    accessTokenExpiresAt: timestamp('access_token_expires_at'),
    refreshTokenExpiresAt: timestamp('refresh_token_expires_at'),
    scope: text('scope'),
    password: text('password'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at')
      .defaultNow()
      .$onUpdate(() => new Date())
      .notNull(),
  },
  (table) => [index('account_userId_idx').on(table.userId)],
)

// ========================================
// VERIFICATION TABLE (BetterAuth generated - email verification, password reset)
// ========================================

export const verification = pgTable(
  'verification',
  {
    id: text('id').primaryKey(),
    identifier: text('identifier').notNull(),
    value: text('value').notNull(),
    expiresAt: timestamp('expires_at').notNull(),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at')
      .defaultNow()
      .$onUpdate(() => new Date())
      .notNull(),
  },
  (table) => [index('verification_identifier_idx').on(table.identifier)],
)

// ========================================
// API KEY TABLE (BetterAuth API Keys plugin)
// ========================================

export const apikey = pgTable(
  'apikey',
  {
    id: text('id').primaryKey(),
    name: text('name'),
    start: text('start'), // First few characters for display
    prefix: text('prefix'),
    key: text('key').notNull(), // Hashed API key
    userId: text('user_id')
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),
    organizationId: text('organization_id'),
    enabled: boolean('enabled').default(true).notNull(),
    rateLimitEnabled: boolean('rate_limit_enabled').default(false).notNull(),
    rateLimitTimeWindow: integer('rate_limit_time_window'),
    rateLimitMax: integer('rate_limit_max'),
    requestCount: integer('request_count').default(0).notNull(),
    remaining: integer('remaining'),
    refillInterval: integer('refill_interval'),
    refillAmount: integer('refill_amount'),
    lastRefillAt: timestamp('last_refill_at'),
    lastRequest: timestamp('last_request'),
    expiresAt: timestamp('expires_at'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at')
      .defaultNow()
      .$onUpdate(() => new Date())
      .notNull(),
    permissions: text('permissions'),
    metadata: jsonb('metadata'),
  },
  (table) => [index('apikey_userId_idx').on(table.userId)],
)

// ========================================
// TIER PRICING CONFIG (Admin-managed)
// ========================================

export const tierPricingConfig = pgTable('tier_pricing_config', {
  id: integer('id').primaryKey().generatedAlwaysAsIdentity(),
  tier: text('tier').notNull().unique(),
  multiplier: text('multiplier').notNull().default('1.00'), // Stored as text to preserve precision
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at')
    .defaultNow()
    .$onUpdate(() => new Date())
    .notNull(),
}, (table) => [
  check('tier_pricing_config_tier_check', sql`${table.tier} IN ('economy', 'standard', 'premium')`),
])

// ========================================
// USER TIER PREFERENCES
// ========================================

export const userTierPreferences = pgTable('user_tier_preferences', {
  userId: text('user_id').primaryKey().references(() => user.id, { onDelete: 'cascade' }),
  enabledTiers: text('enabled_tiers').array().notNull().default(sql`ARRAY['standard']::TEXT[]`),
  modelFallbackChains: text('model_fallback_chains'), // JSON: Record<string, string[]> e.g. { "claude-sonnet-4-6": ["claude-haiku-4-5"] }
  modelConfig: text('model_config').notNull().default('{"default":{"tiers":["standard"]}}'), // JSON: ModelConfig structure (unified tiers + fallbacks)
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at')
    .defaultNow()
    .$onUpdate(() => new Date())
    .notNull(),
}, (table) => [
  index('idx_user_tier_preferences_user_id').on(table.userId),
])

// ========================================
// RELATIONS
// ========================================

export const sessionRelations = relations(session, ({ one }) => ({
  user: one(user, {
    fields: [session.userId],
    references: [user.id],
  }),
}))

export const accountRelations = relations(account, ({ one }) => ({
  user: one(user, {
    fields: [account.userId],
    references: [user.id],
  }),
}))

export const apiKeyRelations = relations(apikey, ({ one }) => ({
  user: one(user, {
    fields: [apikey.userId],
    references: [user.id],
  }),
}))

export const userTierPreferencesRelations = relations(userTierPreferences, ({ one }) => ({
  user: one(user, {
    fields: [userTierPreferences.userId],
    references: [user.id],
  }),
}))

// ========================================
// USER RELATIONS
// ========================================
export const userRelations = relations(user, ({ many, one }) => ({
  sessions: many(session),
  accounts: many(account),
  apiKeys: many(apikey),
  modelStacks: many(modelStack),
  tierPreferences: one(userTierPreferences, {
    fields: [user.id],
    references: [userTierPreferences.userId],
  }),
}))
